import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import BylineWithHeadshot from '@components/BylineWithHeadshot';
import IconDownload from '@components/Images/Rebranding/IconDownload';
import LazyImgixImage from '@components/LazyImgixImage';
import Pill from '@components/Pill';
import ProExclusiveLogo from '@components/ProExclusiveLogo';
import ProjectTitle from '@components/ProjectTitle';
import useCurrentUser from '@hooks/useCurrentUser';
import useDatawrapperResize from '@hooks/useDatawrapperResize';
import useEventTracking from '@hooks/useEventTracking';
import { Project, ProjectMedia } from '@interfaces/projects';
import { download_project_csv_path } from '@routes';
import { ACCESS_PRO } from '@src/constants';
import { ClickTarget, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { dateInLocalTimezone } from '@utils/dates';
import { getImgixSrc } from '@utils/images';

import { AI_INDEX_PROJECT_SLUG } from '.';
import Media from '../Database/Media';

const GOOGLE_SHEET_NAME_FINAL = 'final';
const METHODOLOGY_JUMP_LINK_ANCHOR = '#methodology';

type TopSectionProps = Pick<
  Project,
  'accessLevel' | 'authors' | 'description' | 'slug' | 'title' | 'titleCallout' | 'updatedAt'
> & {
  mediaSourcePath: string;
  projectMedia?: ProjectMedia[];
  sheetName?: string;
  unlocked: boolean;
};

const TopSection = ({
  accessLevel,
  authors,
  description,
  mediaSourcePath,
  projectMedia,
  sheetName = GOOGLE_SHEET_NAME_FINAL,
  slug: projectSlug,
  title,
  titleCallout,
  updatedAt,
  unlocked,
}: TopSectionProps): JSX.Element => {
  const { currentUser } = useCurrentUser();
  useDatawrapperResize();
  const { trackEvent } = useEventTracking();

  const isCurrentUserCorpOrGroupSubscriber = (currentUser as User)?.is_corp_or_group_subscriber;

  return (
    <div className="mx-auto max-w-container-lg">
      <div className="md:flex md:items-stretch">
        <div className="md:flex md:w-3/4 md:min-w-0 md:flex-col md:pr-48">
          {accessLevel >= ACCESS_PRO && (
            <ProExclusiveLogo className="!mb-0 !border-0 !pb-0 md:mx-0 md:max-w-none md:text-left" />
          )}
          <ProjectTitle
            className="!mb-16 md:mx-0 md:!mb-12 md:max-w-none md:text-left"
            title={title}
            titleCallout={titleCallout}
          />
          {authors?.length > 0 && (
            <div className="flex flex-col items-center justify-center gap-8 sm:flex-row sm:gap-0 md:order-3 md:justify-start">
              <BylineWithHeadshot
                authors={authors}
                className="font-suisse-screen text-byline-sm text-primary-black sm:text-byline-lg"
                containerClassName={!unlocked ? 'z-[0]' : ''}
              />{' '}
              <span className="hidden px-12 text-[20px] sm:inline">\</span>{' '}
              <p className="font-suisse-screen text-description-sm font-normal italic text-grayscale-gray">
                Last updated{' '}
                <time dateTime={new Date(updatedAt).toISOString()}>
                  {dateInLocalTimezone(updatedAt)}
                </time>
              </p>
            </div>
          )}
          {projectMedia && projectMedia?.length > 0 ? (
            <div className="flex flex-col flex-wrap items-center justify-between gap-40 md:order-2 md:mt-64 md:flex-row md:items-start">
              <div className="my-18 w-full flex-grow md:w-5/12">{ReactHtmlParser(description)}</div>
              {projectMedia.map((media) => (
                <Media key={media.id} media={media} mediaSourcePath={mediaSourcePath} />
              ))}
            </div>
          ) : (
            <div className="mx-auto mt-16 w-full max-w-[690px] text-center font-noto-serif text-caption-lg font-normal text-primary-black md:order-2 md:mx-0 md:mb-12 md:mt-0 md:max-w-none md:text-left md:text-body-articles">
              {ReactHtmlParser(description)}
            </div>
          )}
        </div>
        <div className="mt-20 flex flex-col justify-center xs:flex-row md:mt-0 md:w-1/4 md:flex-col md:items-start md:gap-12 md:border-0 md:border-l md:border-solid md:border-grayscale-gray md:pl-48 lg:pl-56">
          <Pill
            className="htmlvianotes-synopsis-pill flex items-center justify-center transition-colors duration-200 md:!mb-0 md:!mr-0 md:!h-[34px] md:!border-grayscale-gray md:!px-12 md:!py-0 md:!text-[12px]/[1.5] md:!text-grayscale-gray md:hover:!border-primary-magenta"
            href={METHODOLOGY_JUMP_LINK_ANCHOR}
            text="Read our methodology"
          />
          {isCurrentUserCorpOrGroupSubscriber && (
            <Pill
              className="group flex items-center justify-center transition-colors duration-200 md:!mb-0 md:!mr-0 md:!h-[34px] md:!border-grayscale-gray md:!px-12 md:!py-0 md:!text-[12px]/[1.5] md:!text-grayscale-gray md:hover:!border-primary-magenta"
              href={download_project_csv_path(projectSlug, {
                sheet_name: sheetName,
              })}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.CSVDownloadLink }))}
              text="Download CSV"
              elementIcon={
                <IconDownload className="mr-6 text-primary-magenta transition-colors duration-200 group-hover:text-white" />
              }
              download
            />
          )}
        </div>
      </div>
      {!unlocked && projectSlug === AI_INDEX_PROJECT_SLUG && (
        <LazyImgixImage
          src={getImgixSrc({
            path: 'projects/aiindex/data_snippet.png',
            isEnvDependent: false,
          })}
          alt={title}
          className="mt-32 block w-full sm:mt-48"
        />
      )}
    </div>
  );
};

export default TopSection;
